import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SchemaFieldDataType} from 'folder-items/FileConstants';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
import {ProcedureSchemaFieldTypeTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {ANY_ENTITY, FieldType, RequiredLink, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getTagSchemaSummaryFromListingWithFields} from 'tag-schema/util/TagSchemaUtil';
import ErrorSubclass from 'util/ErrorSubclass';
import {typeToName} from 'util/NameUtil';

import {TagSchemaListingWithFieldsGraphQL} from '../../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';
import {
  DataParameterType,
  DEFAULT_DATA_PARAMETER_TYPE,
  MaterialType,
  ProcedureSchemaFieldType,
} from './procedureSchemaFieldConstants';

type ValidFolderItemCategory = 'basic_folder_item' | 'protein' | 'sequence' | 'molecule' | 'mixture';

const VALID_FOLDER_ITEM_CATEGORY_TYPES: Array<ValidFolderItemCategory> = [
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.MOLECULE,
  ModelTypeConstants.MIXTURE,
];

export class SchemaHydrationError extends ErrorSubclass {}

function hydrateRequiredLinkFromId(
  tagSchemaId: string,
  tagSchemas: Array<TagSchemaListingWithFieldsGraphQL>
): RequiredLink | null {
  const schema = tagSchemas.find(({id}) => id === tagSchemaId);

  if (schema == null) {
    /**
     * Note that we throw a custom error rather than calling `assert`. This error will happen during normal
     * situations (search for usages of the error class), and trapping the error this way allows us to control
     * whether the message is logged to Sentry.
     *
     * If the error is not swallowed inside a custom catch handler, it bubbles up to `window.onunhandledrejection`,
     * where it will be (correctly) logged to Sentry.
     */
    throw new SchemaHydrationError(`Unknown schema when hydrating procedure: ${tagSchemaId}`);
  }
  return {
    tagSchema: getTagSchemaSummaryFromListingWithFields(schema),
    entitySchemaFieldPath: null,
    folderItemType: null,
    schemaInterface: null,
  };
}

export function convertTypedValueToMaterialType(
  {fieldDefinition}: ProcedureSchemaFieldTypeTypedValue,
  tagSchemas: Array<TagSchemaListingWithFieldsGraphQL>
): MaterialType {
  if (fieldDefinition.linkedTagSchemaId != null) {
    // Specific entity schema
    return {
      type: FieldType.BIOENTITY_LINK,
      requiredLink: hydrateRequiredLinkFromId(fieldDefinition.linkedTagSchemaId, tagSchemas),
      unit: null,
    };
  } else if (fieldDefinition.linkedFolderItemType == null) {
    if (fieldDefinition.fieldType === FieldType.TEXT) {
      // Text
      return {
        type: FieldType.TEXT,
        requiredLink: null,
        unit: null,
      };
    } else if (fieldDefinition.fieldType === FieldType.BIOENTITY_LINK) {
      // Any entity
      return {
        type: FieldType.BIOENTITY_LINK,
        requiredLink: {
          folderItemType: null,
          tagSchema: null,
          entitySchemaFieldPath: null,
          schemaInterface: null,
        },
        unit: null,
      };
    }
    assert(false, 'Unknown material', {fieldDefinition});
  } else if (VALID_FOLDER_ITEM_CATEGORY_TYPES.includes(fieldDefinition.linkedFolderItemType)) {
    // Protein / Sequence / Molecule
    return {
      type: FieldType.BIOENTITY_LINK,
      requiredLink: {
        folderItemType: fieldDefinition.linkedFolderItemType,
        tagSchema: null,
        entitySchemaFieldPath: null,
        schemaInterface: null,
      },
      unit: null,
    };
  }
  assert(false, 'Unknown material', {fieldDefinition});
}

export function convertMaterialTypeToTypedValue(
  materialType: MaterialType
): ProcedureSchemaFieldTypeTypedValue {
  const type: string = materialType.type;
  if (type === FieldType.TEXT) {
    // Text
    assert(materialType.requiredLink == null, 'Text material should not have a required link');
    return {
      type: BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE,
      displayName: 'Text',
      fieldDefinition: {
        fieldType: FieldType.TEXT,
        linkedFolderItemType: null,
        linkedTagSchemaId: null,
      },
    };
  }

  const requiredLink = materialType.requiredLink;
  assert(type === FieldType.BIOENTITY_LINK, 'Expected a bioentity link type', {
    materialType,
  });
  assert(requiredLink != null, 'Expected a required link');

  const folderItemType = (requiredLink as {folderItemType: ValidFolderItemCategory | null}).folderItemType;
  const linkedTagSchema = (requiredLink as {tagSchema: {id: string; name: string} | null}).tagSchema;

  if (folderItemType == null) {
    if (linkedTagSchema == null) {
      assert(
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        folderItemType === null && linkedTagSchema === null,
        'Expected an empty requiredLink'
      );
      // Any entity
      return {
        type: BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE,
        displayName: ANY_ENTITY.name,
        fieldDefinition: {
          fieldType: FieldType.BIOENTITY_LINK,
          linkedFolderItemType: null,
          linkedTagSchemaId: null,
        },
      };
    }
    // A specific entity schema
    return {
      type: BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE,
      displayName: linkedTagSchema.name,
      fieldDefinition: {
        fieldType: FieldType.BIOENTITY_LINK,
        linkedFolderItemType: null,
        linkedTagSchemaId: linkedTagSchema.id,
      },
    };
  } else if (VALID_FOLDER_ITEM_CATEGORY_TYPES.includes(folderItemType)) {
    // Protein / Sequence / Molocule
    return {
      type: BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE,
      displayName: typeToName(type),
      fieldDefinition: {
        fieldType: FieldType.BIOENTITY_LINK,
        linkedFolderItemType: folderItemType,
        linkedTagSchemaId: null,
      },
    };
  }
  assert(false, 'Unknown material type', {materialType});
}

export function convertMaterialTypeToSchemaFieldDataType(
  materialType: MaterialType,
  isMulti: boolean
): SchemaFieldDataType {
  return {
    fieldType: materialType.type,
    requiredLink: materialType.requiredLink,
    isRequired: true,
    isMulti,
  };
}

export function convertTypedValueToDataParameterType({
  fieldDefinition,
}: ProcedureSchemaFieldTypeTypedValue): DataParameterType {
  const fieldType = fieldDefinition.fieldType as FieldType.TEXT | FieldType.FLOAT;

  if (fieldDefinition.linkedTagSchemaId == null && [FieldType.TEXT, FieldType.FLOAT].includes(fieldType)) {
    return {
      type: fieldType,
      requiredLink: null,
      unit: null,
    };
  }
  return DEFAULT_DATA_PARAMETER_TYPE;
}

export function convertDataParameterTypeToTypedValue(
  dataParameterType: DataParameterType
): ProcedureSchemaFieldTypeTypedValue {
  const type = dataParameterType.type;
  if (type === FieldType.TEXT || type === FieldType.FLOAT) {
    return {
      type: BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE,
      displayName: type === FieldType.TEXT ? 'Text' : 'Decimal',
      fieldDefinition: {
        fieldType: type,
        linkedFolderItemType: null,
        linkedTagSchemaId: null,
      },
    };
  }
  assert(false, 'Misconfigured data parameter type');
  return {
    type: BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE,
    displayName: 'Text',
    fieldDefinition: {
      fieldType: FieldType.TEXT,
      linkedFolderItemType: null,
      linkedTagSchemaId: null,
    },
  };
}

// The SchemaField representation is needed by FieldDefinitionInputOrDisplay to track the current selection
export function convertToSchemaField(schemaFieldType: ProcedureSchemaFieldType): SchemaField {
  return {
    apiId: '',
    dbId: 0,
    id: 0,
    archiveRecord: null,
    name: '',
    displayName: '',
    systemName: '',
    isRequired: false,
    isComputed: false,
    isConvertedFromLink: false,
    isSnapshot: false,
    isIntegration: false,
    isMulti: false,
    isParentLink: false,
    description: null,
    schemaFieldSelectorId: null,
    fieldType: schemaFieldType.type,
    requiredLink: schemaFieldType.requiredLink,
  };
}
